import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { NavigationHeader } from 'components/NavigationHeader';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { FontFamilySemibold, theme } from 'lib/theme';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  doReportingGetReportCsv,
  doReportingMergeDraftFilterValues,
  doReportingShowReportFilterModal,
  doReportingSetReportVisualsSize,
  doReportingGetReportListData,
} from 'store/actions';
import {
  selectRptReportFilterOptions,
  selectRptReportFilterValues,
  selectRptReportHasVisuals,
  selectRptReportName,
  selectRptReportTotalCount,
  selectRptReportVisualsSize,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { Menu } from 'components/Menu';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { IconButton } from 'components/IconButton';
import { QuickOptionField } from 'components/Field/QuickOptionField';
import { TextField } from 'components/Field/TextField';
import { TEXT_SIZE_SMALL, Txt } from 'components/Txt';
import { View } from 'react-native';
import { noop } from 'lodash';
import { smartText } from 'lib/str';
import { ReportingVisualsSizeEnum } from 'types/types';
import { SidebarShowButton } from 'componentsAware/SidebarShowButton';

type Report = ReportTypes[ReportEnum.checkscompleted];

export const Header: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const [widthSize, setWidthSize] = React.useState<
    'small' | 'medium' | 'large'
  >('small');
  const visualsSize = useSelector(selectRptReportVisualsSize);
  const hasVisuals = useSelector((state) =>
    selectRptReportHasVisuals(state, reportKey),
  );
  const reportName = useSelector((state) =>
    selectRptReportName(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );

  const filterValues = useSelector((state) =>
    selectRptReportFilterValues(state, reportKey),
  )! as Report['filterValues'];
  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  )! as Report['filterOptions'];

  const handleLoadReportCsv = React.useCallback(() => {
    dispatch(
      doReportingGetReportCsv({
        key: reportKey,
      }),
    );
  }, [dispatch, reportKey]);

  const handleFilterChange = React.useCallback(
    (mergeFilterValues: Partial<Report['filterValues']>) => {
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: mergeFilterValues,
          loadData: true,
        }),
      );
    },
    [dispatch, reportKey],
  );

  // Search field
  const [showSearch, setShowSearch] = React.useState(
    filterValues.search !== '',
  );
  const isTextFieldFocused = React.useRef(false);
  const shouldFocusSearchFieldRef = React.useRef(false);
  const searchValueRef = React.useRef(filterValues.search);
  searchValueRef.current = filterValues.search;
  const handleSearchChange = React.useCallback(
    (newSearchValue: string) =>
      newSearchValue !== searchValueRef.current &&
      newSearchValue.trim() !== searchValueRef.current
        ? dispatch(
            doReportingMergeDraftFilterValues({
              key: reportKey,
              filterValues: { search: newSearchValue },
              loadData: true,
            }),
          )
        : noop,
    [dispatch, reportKey],
  );
  const handleSearchBlur = React.useCallback(() => {
    isTextFieldFocused.current = false;
    if (!searchValueRef.current) {
      setShowSearch(false);
    }
  }, []);
  const handleSearchFocus = React.useCallback(() => {
    isTextFieldFocused.current = true;
  }, []);
  React.useEffect(() => {
    if (!isTextFieldFocused.current) {
      setShowSearch(filterValues.search !== '');
    }
  }, [filterValues.search]);
  const shouldFocusSearchField = shouldFocusSearchFieldRef.current;
  shouldFocusSearchFieldRef.current = false;

  const filteredOrganisations = filterValues.organisationIds.length
    ? filterOptions.organisations.filter((item) =>
        filterValues.organisationIds.includes(item.value),
      )
    : filterOptions.organisations.length === 1
      ? filterOptions.organisations
      : [];

  return (
    <NavigationHeader
      style={{
        flexDirection: 'row',
        alignItems: 'center',
      }}
      headerStyle={{ alignItems: 'flex-start' }}
      marginRight={0}
      marginLeft={0}
      onLayout={(event) => {
        if (event.nativeEvent.layout.width < 550) {
          setWidthSize('small');
        } else if (event.nativeEvent.layout.width < 700) {
          setWidthSize('medium');
        } else {
          setWidthSize('large');
        }
      }}
    >
      <SidebarShowButton />
      {!showSearch ? (
        <>
          <IconButton
            icon={ICONS.MagnifyingGlassLight}
            onPress={() => {
              shouldFocusSearchFieldRef.current = true;
              setShowSearch(true);
            }}
            style={{ marginRight: theme.paddingHorizontal }}
          />
          <View style={{ flex: 1, justifyContent: 'center' }}>
            <Txt weight="bold" numberOfLines={1} lineHeightNone>
              {reportName}
            </Txt>
            <Txt
              style={{ color: theme.colors.fieldValueText, marginTop: 4 }}
              size="xsmall"
              numberOfLines={1}
              lineHeightNone
            >
              {pluralise(
                reportTotalCount,
                '{x} Result',
                '{x} Results',
                'No Results',
              )}
              {filteredOrganisations.length > 1
                ? ` • ${filteredOrganisations.length} Organisations`
                : filteredOrganisations.length === 1
                  ? ` • ${filteredOrganisations[0]!.name}`
                  : ' • All Organisations'}
            </Txt>
          </View>
        </>
      ) : (
        <TextField
          syncIncomingValue
          value={filterValues.search}
          autoFocus={shouldFocusSearchField}
          autoCorrect={false}
          placeholder={`Search ${reportName}`}
          iconLeft={ICONS.MagnifyingGlassLight}
          onChange={handleSearchChange}
          onBlur={handleSearchBlur}
          onFocus={handleSearchFocus}
          inputStyle={{ minHeight: null }}
          textRight={pluralise(reportTotalCount, '{x}', '{x}', '')}
          clearable
          onChangeDebounceMs={500}
          containerStyle={{
            borderRadius: 4,
            height: 40,
            flex: 1,
            overflow: 'hidden',
            marginRight: theme.paddingHorizontalHalf,
          }}
          disabled={false}
          textRightContainerStyle={{
            backgroundColor: '#fafafa',
            alignSelf: 'stretch',
            justifyContent: 'center',
            paddingHorizontal: theme.paddingHorizontal,
          }}
          textRightStyle={{
            fontFamily: FontFamilySemibold,
            fontSize: TEXT_SIZE_SMALL,
          }}
        />
      )}
      <ViewWithSeparators
        horizontal
        style={{ alignItems: 'center' }}
        separatorMargin={theme.paddingHorizontalHalf / 2}
        separatorColor="transparent"
      >
        {widthSize !== 'small' && (
          <QuickOptionField
            labelNone="All Checks"
            labelAll="All Checks"
            labelMultiple="{x} Checks"
            modalTitle="Checks"
            options={filterOptions.checks.map((item) => ({
              name: item.name,
              value: item.value,
              groupName: item.deleted ? 'Deleted Checks' : 'Current Checks',
            }))}
            values={filterValues.checks}
            enableSelectAll
            buttonMaxWidth={150}
            onChange={(newValue) => handleFilterChange({ checks: newValue })}
            modal
            modalSize="small"
            modalSearchable
            multiSelect
          />
        )}
        {widthSize !== 'small' && (
          <QuickOptionField
            labelNone="All Elements"
            labelAll="All Elements"
            labelMultiple="{x} Elements"
            modalTitle="Elements"
            options={filterOptions.elements.map((item) => ({
              name: item.name,
              value: item.value,
              groupName: item.deleted
                ? `Deleted Elements${!item.checks ? ' Without Checks' : ''}`
                : `Current Elements${!item.checks ? ' Without Checks' : ''}`,
            }))}
            values={filterValues.elements}
            enableSelectAll
            buttonMaxWidth={150}
            onChange={(newValue) => handleFilterChange({ elements: newValue })}
            modal
            modalSize="small"
            modalSearchable
            multiSelect
          />
        )}
        {widthSize === 'large' && (
          <QuickOptionField
            options={filterOptions.statuses.map((item) => ({
              name: item.name,
              value: item.value,
              icon: item.icon,
              iconColor: `#${item.color}`,
            }))}
            values={filterValues.statuses}
            labelNone="All Statuses"
            labelAll="All Statuses"
            labelMultiple="{x} Statuses"
            enableSelectAll
            menuWidth={180}
            buttonMaxWidth={150}
            onChange={(newValue) => handleFilterChange({ statuses: newValue })}
          />
        )}
        <IconButton
          icon={ICONS.SlidersUpLight}
          onPress={() =>
            dispatch(doReportingShowReportFilterModal({ key: reportKey }))
          }
          showBadge={
            !!filterValues.checks.length ||
            !!filterValues.elements.length ||
            !!filterValues.organisationIds.length ||
            !!filterValues.statuses.length ||
            !!filterValues.dateFrom ||
            !!filterValues.dateTo
          }
        />
        <Menu
          iconButtonConfig={{ icon: ICONS.EllipsisLight }}
          options={[
            {
              text: 'Export List as CSV',
              icon: ICONS.ArrowDownLight,
              onPress: handleLoadReportCsv,
            },
            <CardRow
              key="visualsOptionsRow"
              label="Visuals Options"
              disabled={!hasVisuals}
              navigationIcon={ICONS.ChartBarDuotone}
              navigationIconColor={theme.colors.black}
              style={{
                width: 250,
                minHeight: 44,
                backgroundColor: '#fafafa',
              }}
              hoveredStyle={{ backgroundColor: '#f1f1f1' }}
              childrenStyle={{}}
              menuOptions={
                hasVisuals
                  ? [
                      {
                        text: 'Large Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.large
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.large,
                            }),
                          ),
                      },
                      {
                        text: 'Small Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.small
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.small,
                            }),
                          ),
                      },
                      {
                        text: 'Hide Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.none
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.none,
                            }),
                          ),
                      },
                      smartText(
                        'Some reports include visuals like charts and graphs. They only appear if they have data to show',
                      ),
                    ]
                  : undefined
              }
              showMenuOnPress
              hideMenuButton
            />,
            '-',
            {
              text: 'Reload',
              icon: ICONS.RotateRightLight,
              onPress: () =>
                dispatch(
                  doReportingGetReportListData({
                    key: reportKey,
                    nextPage: false,
                    closeModals: false,
                    overlayLoader: true,
                  }),
                ),
            },
          ]}
        />
      </ViewWithSeparators>
    </NavigationHeader>
  );
};
